import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import axios from "axios";
import { getCallAgentStatsGraph } from "../../dashboards/redux/dashboardApi";
import { getWeeklyTooltipDetails, formatWeekLabelOnXAxis } from "../utils/formatters";
import { VIEW_BTN_ACTIVE, VIEW_BTN_INACTIVE, CARD_TITLE } from "../utils/styles";
import "../analyticaldashboard.module.css";

const parseFormattedWeeklyLabel = (label) => {
    if (!label || typeof label !== "string") return null;
    const parts = label.split("\n");
    if (parts.length < 2) return null;
    const yearStr = parts[1].trim();
    const rangeStr = parts[0].trim();
    const rangeParts = rangeStr.split("-").map(s => s.trim());
    if (rangeParts.length < 2) return null;

    const startPart = rangeParts[0];
    const endPart = rangeParts[1];

    const endSubParts = endPart.split(" ").filter(Boolean);
    if (endSubParts.length < 2) return null;
    const endMonthName = endSubParts[1];

    const startSubParts = startPart.split(" ").filter(Boolean);
    let startDay, startMonthName;
    if (startSubParts.length === 1) {
        startDay = parseInt(startSubParts[0], 10);
        startMonthName = endMonthName;
    } else {
        startDay = parseInt(startSubParts[0], 10);
        startMonthName = startSubParts[1];
    }

    const monthMap = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
    };
    const monthIndex = monthMap[startMonthName.toLowerCase().substring(0, 3)];
    const year = parseInt(yearStr, 10);
    if (isNaN(startDay) || isNaN(year) || monthIndex === undefined) return null;

    return new Date(year, monthIndex, startDay);
};

const formatWeeklyLabel = (startDate) => {
    const pad = n => String(n).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    const weekEnd = new Date(startDate);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const sameMonth = startDate.getMonth() === weekEnd.getMonth() && startDate.getFullYear() === weekEnd.getFullYear();

    if (sameMonth) {
        return `${pad(startDate.getDate())} - ${pad(weekEnd.getDate())} ${months[weekEnd.getMonth()]}\n${weekEnd.getFullYear()}`;
    }

    return `${pad(startDate.getDate())} ${months[startDate.getMonth()]} - ${pad(weekEnd.getDate())} ${months[weekEnd.getMonth()]}\n${weekEnd.getFullYear()}`;
};

const OutboundCalls = ({ agent, startDate, endDate, user, batchName, industry }) => {
    const dispatch = useDispatch();
    const [view, setView] = useState("weekly");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [visibleLines, setVisibleLines] = useState({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest(".show-hide-lines-container")) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownOpen]);

    const toggleLine = (name) => {
        setVisibleLines(prev => ({
            ...prev,
            [name]: prev[name] === false ? true : false
        }));
    };

    const { callAgentStatsGraph: data, isCallAgentStatsGraphLoading: isLoading } = useSelector(
        (state) => state.dashboard,
        shallowEqual
    );

    const { adminBaseURL } = useSelector(
        (state) => state.environnment.environmentLists,
        shallowEqual
    );

    const [dailyData, setDailyData] = useState(null);

    useEffect(() => {
        if (startDate && !endDate) {
            return;
        }
        dispatch(getCallAgentStatsGraph({ agent, startDate, endDate, view, user, batchName, industry }));
    }, [dispatch, agent, startDate, endDate, view, user, batchName, industry]);

    useEffect(() => {
        if (startDate && !endDate) {
            return;
        }
        if (view === "weekly" && adminBaseURL) {
            const params = new URLSearchParams();
            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                params.append("from", start.getTime());
            }
            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                params.append("to", end.getTime());
            }
            if (agent) {
                params.append("filterType", agent);
            }
            params.append("view", "daily");
            if (user) params.append("created_by", user);
            if (batchName) params.append("batch_name", batchName);
            if (industry) params.append("IndustryName", industry);

            axios({
                method: "GET",
                url: `${process.env.REACT_APP_HOST}${adminBaseURL}/dashboard/callAgentStatsGraph?${params.toString()}`,
                headers: {
                    "Content-Type": "application/json",
                },
            }).then(({ data }) => {
                if (data && data.code === 200) {
                    setDailyData(data.data);
                } else {
                    setDailyData(null);
                }
            }).catch((err) => {
                console.error("Error fetching daily data:", err);
                setDailyData(null);
            });
        } else {
            setDailyData(null);
        }
    }, [agent, startDate, endDate, view, user, batchName, industry, adminBaseURL]);

    const { isNoData, finalSeries, options, skeletonSeries, skeletonOptions, allowedSeries } = React.useMemo(() => {
        const rawSeries = data && data.series ? data.series : [];

        // Keep "Total Calls" as display name for all agents
        const mappedSeries = [];
        const seenNames = new Set();
        rawSeries.forEach(s => {
            const name = s.name;
            if (!seenNames.has(name)) {
                seenNames.add(name);
                mappedSeries.push({ ...s, name });
            }
        });

        const allowedSeries = ["Total Calls", "Calls Initiated", "Calls Concluded", "Failed Calls"];

        const series = allowedSeries
            .map(name => mappedSeries.find(s => s.name === name))
            .filter(Boolean);

        let rawCategories = data && data.categories ? data.categories : [];
        let finalSeries = series;
        let finalCategories = rawCategories;

        if (view === "weekly" && rawCategories.length >= 2) {
            const parsedDates = rawCategories.map(cat => parseFormattedWeeklyLabel(cat)).filter(Boolean);
            if (parsedDates.length >= 2) {
                const allDates = [];
                let current = new Date(parsedDates[0]);
                const end = parsedDates[parsedDates.length - 1];
                while (current <= end) {
                    allDates.push(new Date(current));
                    current.setDate(current.getDate() + 7);
                }

                finalCategories = allDates.map(d => formatWeeklyLabel(d));
                finalSeries = series.map(s => {
                    const newData = allDates.map(d => {
                        const label = formatWeeklyLabel(d);
                        const idx = rawCategories.indexOf(label);
                        if (idx !== -1) {
                            return s.data[idx];
                        }
                        return 0;
                    });
                    return {
                        ...s,
                        data: newData
                    };
                });
            }
        }

        const dataLength = finalSeries.length > 0
            ? Math.max(...finalSeries.map(s => s.data?.length || 0))
            : 0;

        // Find first non-zero index in any of the allowed series
        let firstNonZeroIdx = -1;
        for (let i = 0; i < dataLength; i++) {
            const hasValue = finalSeries.some(s => {
                const val = s.data[i];
                return val !== null && val !== undefined && Number(val) > 0;
            });
            if (hasValue) {
                firstNonZeroIdx = i;
                break;
            }
        }

        const isNoData = firstNonZeroIdx === -1 || dataLength === 0;

        // Trim leading zero periods
        const shouldTrim = false;
        if (!isNoData && firstNonZeroIdx > 0 && shouldTrim) {
            finalSeries = finalSeries.map(s => ({
                ...s,
                data: s.data.slice(firstNonZeroIdx)
            }));
            finalCategories = finalCategories.slice(firstNonZeroIdx);
        }

        const visibleIndices = new Set();
        if (!isNoData) {
            let limit = 0;
            if (view === "weekly" && finalCategories.length > 7) {
                limit = 6;
            } else if (view === "monthly" && finalCategories.length > 9) {
                limit = 8;
            }

            if (limit > 0) {
                const n = finalCategories.length;
                for (let i = 0; i < limit; i++) {
                    visibleIndices.add(Math.round((i * (n - 1)) / (limit - 1)));
                }
            } else {
                for (let i = 0; i < finalCategories.length; i++) {
                    visibleIndices.add(i);
                }
            }
        }

        // Filter out hidden series for the actual chart rendering
        finalSeries = finalSeries.filter(s => visibleLines[s.name] !== false);

        const colorMap = {
            "Total Calls": "#6f42c1",
            "Calls Initiated": "#0ea5e9",
            "Calls Concluded": "#10b981",
            "Call Backs": "#a855f7",
            "Interested in Future": "#ff9f1c",
            "Failed Calls": "#ef4444",
        };

        const chartColors = finalSeries.map(s => colorMap[s.name] || "#6f42c1");

        // build apexcharts options
        const findCategoryIndex = (val) => {
            if (!val) return -1;
            const normalizedVal = String(val).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            return finalCategories.findIndex(cat => {
                const normalizedCat = String(cat).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                return normalizedCat === normalizedVal;
            });
        };

        const options = {
            chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "Inter, sans-serif",
            },
            colors: chartColors.length > 0 ? chartColors : ["#6f42c1"],
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: 2.5 },
            markers: {
                size: 4,
                strokeWidth: 0,
                colors: chartColors.length > 0 ? chartColors : ["#6f42c1"],
                hover: { size: 6 }
            },
            xaxis: {
                categories: finalCategories,
                tickAmount: undefined,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: {
                    rotate: 0,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    showDuplicates: false,
                    minHeight: 40,
                    maxHeight: 60,
                    style: {
                        colors: "#A1A5B7",
                        fontSize: "10px",
                        fontWeight: 500,
                        fontFamily: "Inter, sans-serif"
                    },
                    formatter: (val) => {
                        const idx = findCategoryIndex(val);
                        if (idx !== -1 && !visibleIndices.has(idx)) {
                            return "";
                        }

                        if (typeof val === 'string') {
                            if (val.includes("\n")) {
                                return val.split("\n");
                            }
                            const yearMatch = val.match(/[\s\n](\d{4})$/);
                            if (yearMatch) {
                                const year = yearMatch[1];
                                const dateRange = val.replace(/[\s\n]\d{4}$/, "").trim();
                                return [dateRange, year];
                            }
                            if (finalCategories.length > 12) {
                                if (val.includes('-W')) {
                                    const parts = val.split('-W');
                                    const year = parseInt(parts[0], 10);
                                    const week = parseInt(parts[1], 10);
                                    const simple = new Date(year, 0, 4);
                                    const dow = simple.getDay();
                                    const isoMon = new Date(simple.getFullYear(), 0, 4 - ((dow + 6) % 7));
                                    const wStart = new Date(isoMon.getTime() + (week - 1) * 7 * 86400000);
                                    const wEnd = new Date(wStart.getTime() + 6 * 86400000);
                                    const pad = n => n.toString().padStart(2, "0");
                                    const sm = wStart.toLocaleString("en-US", { month: "short" });
                                    const em = wEnd.toLocaleString("en-US", { month: "short" });
                                    const sd = wStart.getDate(), ed = wEnd.getDate();
                                    const range = sm === em
                                        ? `${pad(sd)}-${pad(ed)} ${em}`
                                        : `${pad(sd)} ${sm}-${pad(ed)} ${em}`;
                                    return [range, `${year}`];
                                }
                                return val;
                            }

                            const formatted = formatWeekLabelOnXAxis(val);
                            if (Array.isArray(formatted) && formatted.length === 2) {
                                return formatted;
                            }
                            return formatted;
                        }
                        return val;
                    }
                }
            },
            yaxis: {
                labels: {
                    style: { colors: "#A1A5B7", fontSize: "11px", fontWeight: 500 }
                }
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.15,
                    opacityTo: 0.02,
                    stops: [0, 90, 100]
                }
            },
            legend: { show: false },
            grid: {
                borderColor: "#E4E6EF",
                strokeDashArray: 4,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
                padding: { left: 35, right: 35, bottom: 0, top: 0 }
            },
            tooltip: {
                theme: "light",
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const categoryVal = finalCategories && finalCategories[dataPointIndex]
                        ? finalCategories[dataPointIndex]
                        : (w.globals.labels ? w.globals.labels[dataPointIndex] : "");

                    const weekStart = parseFormattedWeeklyLabel(categoryVal);
                    if (view === "weekly" && weekStart && dailyData && dailyData.buckets) {
                        const details = getWeeklyTooltipDetails(categoryVal);
                        let tooltipHtml = `
                            <div class="p-3" style="font-family: Inter, sans-serif; min-width: 280px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                                <div style="font-weight: 700; font-size: 13px; color: #181c32; margin-bottom: 2px;">Weekly Daily Breakdown</div>
                                <div style="font-size: 11px; color: #7e8299; margin-bottom: 10px; font-weight: 500;">${details ? details.dateRange : categoryVal.replace("\n", " ")}</div>
                                <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px;">
                                    <thead>
                                        <tr style="border-bottom: 1px solid #EFF2F5; color: #7E8299; font-weight: 600;">
                                            <th style="padding: 4px 6px 4px 0; text-align: left;">Day</th>
                                            <th style="padding: 4px 6px; text-align: center; color: #6f42c1;">Total</th>
                                            <th style="padding: 4px 6px; text-align: center; color: #0ea5e9;">Initiated</th>
                                            <th style="padding: 4px 6px; text-align: center; color: #10b981;">Concluded</th>
                                            <th style="padding: 4px 6px; text-align: center; color: #ef4444;">Failed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                        `;

                        const pad = n => String(n).padStart(2, "0");
                        for (let i = 0; i < 7; i++) {
                            const d = new Date(weekStart);
                            d.setDate(d.getDate() + i);
                            const dayOfWeek = d.getDay();
                            if (dayOfWeek === 0 || dayOfWeek === 6) {
                                continue;
                            }
                            const yyyy = d.getFullYear();
                            const mm = pad(d.getMonth() + 1);
                            const dd = pad(d.getDate());
                            const dateStr = `${yyyy}-${mm}-${dd}`;

                            const idx = dailyData.buckets.indexOf(dateStr);
                            const sData = {};
                            if (idx !== -1) {
                                dailyData.series.forEach(s => {
                                    sData[s.name] = s.data[idx] || 0;
                                });
                            }

                            const totalCalls = sData["Total Calls"] || 0;
                            const callsInitiated = sData["Calls Initiated"] || 0;
                            const callsConcluded = sData["Calls Concluded"] || 0;
                            const failedCalls = sData["Failed Calls"] !== undefined 
                                ? sData["Failed Calls"] 
                                : Math.max(0, totalCalls - callsInitiated);

                            const shortDayName = d.toLocaleDateString("en-US", { weekday: "short" });
                            const dayNum = d.getDate();
                            const isToday = new Date().toDateString() === d.toDateString();

                            tooltipHtml += `
                                <tr style="border-bottom: 1px solid #F3F4F6; background-color: ${isToday ? '#F8F5FF' : 'transparent'}; font-weight: ${isToday ? '600' : 'normal'};">
                                    <td style="padding: 6px 6px 6px 0; color: ${isToday ? '#6f42c1' : '#5E6278'}; font-weight: 500;">
                                        ${shortDayName} ${dayNum}
                                    </td>
                                    <td style="padding: 6px 6px; text-align: center; color: #181c32; font-weight: 600;">
                                        ${totalCalls.toLocaleString()}
                                    </td>
                                    <td style="padding: 6px 6px; text-align: center; color: #5E6278;">
                                        ${callsInitiated.toLocaleString()}
                                    </td>
                                    <td style="padding: 6px 6px; text-align: center; color: #5E6278;">
                                        ${callsConcluded.toLocaleString()}
                                    </td>
                                    <td style="padding: 6px 6px; text-align: center; color: #5E6278;">
                                        ${failedCalls.toLocaleString()}
                                    </td>
                                </tr>
                            `;
                        }

                        tooltipHtml += `
                                    </tbody>
                                </table>
                            </div>
                        `;
                        return tooltipHtml;
                    } else {
                        const details = getWeeklyTooltipDetails(categoryVal);
                        let tooltipHtml = `<div class="p-3" style="font-family: Inter, sans-serif; min-width: 170px;">`;
                        if (details) {
                            tooltipHtml += `
                                <div style="font-weight: 700; font-size: 12px; color: #181c32; margin-bottom: 2px;">${details.title}</div>
                                <div style="font-size: 11px; color: #7e8299; margin-bottom: 8px; font-weight: 500;">${details.dateRange}</div>
                            `;
                        } else {
                            tooltipHtml += `
                                <div style="font-weight: 700; font-size: 12px; color: #181c32; margin-bottom: 8px;">${categoryVal}</div>
                            `;
                        }

                        w.config.series.forEach((s, idx) => {
                            const color = w.globals.colors[idx];
                            const val = s.data[dataPointIndex];
                            tooltipHtml += `
                                <div style="display: flex; align-items: center; justify-content: space-between; font-size: 11px; margin-top: 6px; gap: 16px;">
                                  <div style="display: flex; align-items: center;">
                                    <span style="background-color: ${color}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                                    <span style="color: #5e6278; font-weight: 500;">${s.name}</span>
                                  </div>
                                  <span style="color: #181c32; font-weight: 700;">${val !== null && val !== undefined ? val.toLocaleString() : 0}</span>
                                </div>
                            `;
                        });

                        tooltipHtml += `</div>`;
                        return tooltipHtml;
                    }
                }
            }
        };

        const firstSeriesName = "Total Calls";

        const skeletonSeries = [
            { name: firstSeriesName, data: [35, 55, 45, 65, 48, 58, 50] },
            { name: "Calls Initiated", data: [18, 32, 25, 40, 28, 35, 30] },
            { name: "Calls Concluded", data: [6, 15, 10, 20, 12, 16, 14] },
            { name: "Failed Calls", data: [12, 8, 11, 6, 9, 8, 7] }
        ].filter(s => visibleLines[s.name] !== false);

        const skeletonOptions = {
            chart: {
                type: "area",
                height: 180,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false }
            },
            colors: ["#E4E6EF", "#EBEDF3", "#F3F4F6", "#F9FAFB"],
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.15,
                    opacityTo: 0.02,
                    stops: [0, 100]
                }
            },
            dataLabels: { enabled: false },
            stroke: {
                curve: "smooth",
                width: 2,
                colors: ["#D1D5DB", "#E4E6EF", "#EFEFEF", "#F3F4F6"]
            },
            grid: {
                borderColor: "#E4E6EF",
                strokeDashArray: 4,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
                padding: { left: 35, right: 35, bottom: 0, top: 0 }
            },
            xaxis: {
                categories: ["W1", "W2", "W3", "W4", "W5", "W6", "W7"],
                labels: {
                    style: { colors: "#D1D5DB", fontSize: "10px", fontWeight: 500 }
                },
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: {
                labels: {
                    style: { colors: "#D1D5DB", fontSize: "10px", fontWeight: 500 }
                }
            },
            tooltip: { enabled: false },
            legend: { show: false }
        };

        return { isNoData, finalSeries, finalCategories, options, skeletonSeries, skeletonOptions, allowedSeries };
    }, [data, agent, view, visibleLines, dailyData]);

    const isFilterDisabled = isLoading || !data;

    useEffect(() => {
        if (isFilterDisabled) {
            setIsDropdownOpen(false);
        }
    }, [isFilterDisabled]);

    return (
        <div className="card card-custom h-100 shadow-sm border-0 bg-white rounded-lg p-5 p-md-6">
            <div className="card-header border-0 p-0 mb-5 d-flex justify-content-between align-items-center">
                <h3
                    className="card-title font-weight-bolder text-dark font-size-h5 m-0"
                    style={CARD_TITLE}
                >
                    {view === "weekly" ? "Weekly" : "Monthly"} Outbound Calls
                </h3>
                <div className="d-flex align-items-center show-hide-lines-container" style={{ gap: "8px" }}>

                    {/* ── Show/Hide Lines filter button ─────────────────── */}
                    <div style={{ position: "relative" }}>
                        <button
                            type="button"
                            disabled={isFilterDisabled}
                            onClick={() => {
                                if (!isFilterDisabled) {
                                    setIsDropdownOpen(!isDropdownOpen);
                                }
                            }}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "4px 12px",
                                borderRadius: "4px",
                                border: isFilterDisabled
                                    ? "1px solid #EFF2F5"
                                    : `1px solid ${isDropdownOpen ? "#6f42c1" : "#E4E6EF"}`,
                                backgroundColor: isFilterDisabled
                                    ? "#F3F6F9"
                                    : (isDropdownOpen ? "#F8F5FF" : "#FFFFFF"),
                                color: isFilterDisabled
                                    ? "#B5B5C3"
                                    : (isDropdownOpen ? "#6f42c1" : "#5E6278"),
                                fontSize: "0.82rem",
                                fontWeight: "600",
                                cursor: isFilterDisabled ? "not-allowed" : "pointer",
                                transition: "all 0.15s ease",
                                whiteSpace: "nowrap",
                                boxShadow: !isFilterDisabled && isDropdownOpen ? "0 0 0 3px rgba(111,66,193,0.08)" : "none"
                            }}
                        >
                            {/* Filter icon */}
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <line x1="4" y1="6" x2="20" y2="6" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                                <line x1="11" y1="18" x2="13" y2="18" />
                            </svg>
                            Lines

                            {/* Chevron */}
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{
                                    transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.18s ease"
                                }}
                            >
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.6"
                                    strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* ── Dropdown panel ──────────────────────────────── */}
                        {isDropdownOpen && (
                            <div style={{
                                position: "absolute",
                                top: "calc(100% + 6px)",
                                right: 0,
                                zIndex: 1000,
                                minWidth: "200px",
                                backgroundColor: "#ffffff",
                                border: "1px solid #E4E6EF",
                                borderRadius: "10px",
                                boxShadow: "0 8px 24px rgba(82,63,105,0.12)",
                                overflow: "hidden"
                            }}>
                                {/* Panel header */}
                                <div style={{
                                    padding: "10px 14px 8px",
                                    borderBottom: "1px solid #F3F4F6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}>
                                    <span style={{
                                        fontSize: "0.72rem",
                                        fontWeight: "700",
                                        color: "#3F4254",
                                        letterSpacing: "0.6px",
                                        textTransform: "uppercase"
                                    }}>Chart Lines</span>
                                    <button
                                        onClick={() => {
                                            const anyHidden = allowedSeries.some(n => visibleLines[n] === false);
                                            if (anyHidden) {
                                                setVisibleLines({});
                                            } else {
                                                const allHidden = {};
                                                allowedSeries.forEach(n => { allHidden[n] = false; });
                                                // keep at least one visible – reset instead
                                                setVisibleLines({});
                                            }
                                        }}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            fontSize: "0.84rem",
                                            fontWeight: "600",
                                            color: "#6f42c1"
                                        }}
                                    >Show All</button>
                                </div>

                                {/* Line items */}
                                <div style={{ padding: "6px 8px 8px" }}>
                                    {allowedSeries.map((name) => {
                                        const colorMapLocal = {
                                            "Total Calls": "#6f42c1",
                                            "Calls Initiated": "#0ea5e9",
                                            "Calls Concluded": "#10b981",
                                            "Call Backs": "#a855f7",
                                            "Interested in Future": "#ff9f1c",
                                            "Failed Calls": "#ef4444",
                                        };
                                        const color = isNoData ? "#B5B5C3" : (colorMapLocal[name] || "#6f42c1");
                                        const isChecked = visibleLines[name] !== false;
                                        return (
                                            <div
                                                key={name}
                                                onClick={() => toggleLine(name)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    padding: "7px 6px",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                    transition: "background 0.12s ease",
                                                    opacity: isChecked ? 1 : 0.45
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = "#F8F5FF"}
                                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                            >
                                                {/* Color swatch — looks like a mini chart line */}
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    flexShrink: 0
                                                }}>
                                                    <div style={{
                                                        width: "20px",
                                                        height: "3px",
                                                        borderRadius: "2px",
                                                        backgroundColor: isChecked ? color : "#D1D5DB"
                                                    }} />
                                                    <div style={{
                                                        width: "6px",
                                                        height: "6px",
                                                        borderRadius: "50%",
                                                        backgroundColor: isChecked ? color : "#D1D5DB",
                                                        border: `2px solid ${isChecked ? color : "#D1D5DB"}`
                                                    }} />
                                                </div>
                                                {/* Label */}
                                                <span style={{
                                                    flex: 1,
                                                    fontSize: "0.8rem",
                                                    fontWeight: "600",
                                                    color: isChecked ? "#3F4254" : "#A1A5B7"
                                                }}>{name}</span>
                                                {/* Toggle indicator */}
                                                <div style={{
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "3px",
                                                    border: `1.5px solid ${isChecked ? color : "#D1D5DB"}`,
                                                    backgroundColor: isChecked ? color : "transparent",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                    transition: "all 0.15s ease"
                                                }}>
                                                    {isChecked && (
                                                        <svg width="8" height="6" viewBox="0 0 8 6"
                                                            fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M1 3L3 5L7 1" stroke="#fff"
                                                                strokeWidth="1.5" strokeLinecap="round"
                                                                strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Weekly / Monthly toggle ────────────────────────── */}
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className={`btn btn-sm ${view === "weekly" ? "btn-primary font-weight-bolder" : "btn-light font-weight-bold"}`}
                            onClick={() => setView("weekly")}
                            style={view === "weekly" ? VIEW_BTN_ACTIVE : VIEW_BTN_INACTIVE}
                        >
                            Weekly
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${view === "monthly" ? "btn-primary font-weight-bolder" : "btn-light font-weight-bold"}`}
                            onClick={() => setView("monthly")}
                            style={view === "monthly" ? VIEW_BTN_ACTIVE : VIEW_BTN_INACTIVE}
                        >
                            Monthly
                        </button>
                    </div>
                </div>
            </div>

            {data && (
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "14px", marginBottom: "6px" }}>
                    {(isNoData ? skeletonSeries : finalSeries).map((s, idx) => (
                        <div key={idx} className="d-flex align-items-center">
                            <span
                                style={{ backgroundColor: isNoData ? "#E4E6EF" : (options.colors[idx % options.colors.length]), width: "10px", height: "10px", borderRadius: "50%", display: "inline-block", marginRight: "8px" }}
                            ></span>
                            <span className="font-weight-bold" style={{ fontSize: "0.72rem", color: isNoData ? "#B5B5C3" : "#5E6278" }}>
                                {s.name}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="card-body p-0" style={{ position: "relative", minHeight: "180px" }}>
                {!isLoading && data ? (
                    isNoData ? (
                        <div style={{ pointerEvents: "none" }}>
                            <ReactApexChart options={skeletonOptions} series={skeletonSeries} type="area" height={180} />
                            <div className="text-center mt-2" style={{ fontSize: "0.76rem", color: "#7E8299", fontWeight: 500 }}>
                                No outbound call data available for the selected filters.
                            </div>
                        </div>
                    ) : (
                        <ReactApexChart options={options} series={finalSeries} type="area" height={180} />
                    )
                ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 w-100" style={{ minHeight: "180px" }}>
                        <span className="text-muted" style={{ fontSize: "0.76rem" }}>Loading graph data...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(OutboundCalls);
